import { AUDIT_DAYS, platformAuditTabOf, platformAuditTabs, startOfZonedDay, type AuditEntryDto, type PlatformAuditPageDto, type PlatformAuditTab, type PlatformPeopleDto } from '@dbrain/shared';
import { Icons, Menu, MenuItem } from '@dbrain/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client.js';
import { useCompanyZone } from '../../api/hooks.js';
import { CsvLink, DataTable, FilterMenu, Pagination, SearchBox, useListParams, type Column } from '../../components/admin/DataTable.js';
import { ErrorAlert } from '../../components/ErrorAlert.js';
import { auditView, dayKey } from '@dbrain/app-core/audit-details';
import { PillTabs } from '../admin/AdminLayout.js';
import { AUDIT_ICONS } from '../admin/AuditPage.js';

/**
 * Platform › Denetim kaydı in the bireysel edition (Phase 16c, B2cPlatformDenetim): the platform log with the kurumsal
 * Denetim kaydı's layout and components: rows grouped by day, the columns in line with their headers, tabs by kind with
 * their counts, a search, a person and a date, the CSV of the last 90 days and the pager. Each row says what happened
 * in words (app-core's auditView); conversation content is never here.
 */
function PersonFilter({ person, who, onChange }: { person: string | undefined; who: string | undefined; onChange: (person: { id: string; name: string } | null) => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const found = useQuery({
    queryKey: ['platform', 'users', 'pick', q],
    queryFn: () => api<PlatformPeopleDto>('GET', `/api/platform/users?limit=10${q ? `&q=${encodeURIComponent(q)}` : ''}`),
    placeholderData: keepPreviousData,
  });
  return (
    <Menu
      trigger={
        <>
          <span>
            {t('admin.audit.person')}: {person ? (who ?? t('admin.audit.chosen')) : t('admin.audit.everyone')}
          </span>
          <Icons.chevronDown size={14} />
        </>
      }
      triggerClassName="dt-filter"
      panelClassName="dt-filter__panel"
      data-testid="audit-person"
    >
      <input className="ui-input ui-input--sm person-filter__search" type="search" value={q} placeholder={t('admin.audit.personSearch')} aria-label={t('admin.audit.personSearch')} onChange={(e) => setQ(e.target.value)} data-testid="audit-person-search" />
      <MenuItem aria-pressed={!person} onClick={() => onChange(null)}>
        {t('admin.audit.everyone')}
      </MenuItem>
      {(found.data?.rows ?? []).map((p) => (
        <MenuItem key={p.id} aria-pressed={p.id === person} hint={p.email} onClick={() => onChange({ id: p.id, name: p.displayName })} data-testid="audit-person-option">
          {p.displayName}
        </MenuItem>
      ))}
    </Menu>
  );
}

export function AuditTab() {
  const { t, i18n } = useTranslation();
  const list = useListParams<'tab' | 'person' | 'who'>({ days: '7' });
  const query = new URLSearchParams([...new URLSearchParams(list.query())].filter(([key]) => key !== 'who')).toString();
  const entries = useQuery({ queryKey: ['platform', 'audit', query], queryFn: () => api<PlatformAuditPageDto>('GET', `/api/platform/audit/entries?${query}`), placeholderData: keepPreviousData });
  const data = entries.data;
  const zone = useCompanyZone();
  const days = Number(list.get('days') ?? 7);
  const tab = (platformAuditTabs as readonly string[]).includes(list.get('tab') ?? '') ? (list.get('tab') as PlatformAuditTab) : 'all';
  const today = dayKey(new Date().toISOString(), zone);
  const yesterday = dayKey(new Date(startOfZonedDay(new Date(), zone).getTime() - 1).toISOString(), zone);
  const groupLabel = (key: string) => {
    const [y, mo, d] = key.split('-').map(Number);
    const date = new Date(y as number, (mo as number) - 1, d).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' });
    if (key === today) return t('admin.audit.today', { date });
    if (key === yesterday) return t('admin.audit.yesterday', { date });
    return date;
  };
  const view = (e: AuditEntryDto) => auditView(e, t, i18n.language);
  const columns: Column<AuditEntryDto>[] = [
    { id: 'time', header: t('admin.audit.columns.time'), width: 'xs', cell: (e) => <time dateTime={e.createdAt}>{new Date(e.createdAt).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit', timeZone: zone })}</time> },
    {
      id: 'event',
      header: t('admin.audit.columns.event'),
      // narrower than the kurumsal log's: the details are this log's longest column (B2cPlatformDenetim)
      width: 'lg',
      cell: (e) => {
        const v = view(e);
        const Icon = AUDIT_ICONS[v.icon];
        return (
          <span className={`audit-event${v.failure ? ' audit-event--failure' : ''}`}>
            <span className={`audit-event__icon audit-event__icon--${v.icon}`} aria-hidden="true">
              <Icon size={14} />
            </span>
            <span className="audit-event__title">{v.title}</span>
          </span>
        );
      },
    },
    { id: 'kind', header: t('admin.audit.columns.kind'), width: 'md', cell: (e) => <span className="audit-kind">{t(`b2cBilling.audit.tabs.${platformAuditTabOf(e.action)}`)}</span> },
    { id: 'who', header: t('admin.audit.columns.who'), width: 'md', cell: (e) => view(e).who ?? <span className="muted">{t('audit.system')}</span> },
    { id: 'details', header: t('admin.audit.columns.details'), cell: (e) => <span className="audit-detail">{view(e).detail}</span> },
  ];
  return (
    <div className="stack admin-audit platform-audit" data-testid="platform-audit">
      <p className="muted">{t('b2cBilling.audit.hint')}</p>
      <PillTabs
        label={t('admin.audit.tabsLabel')}
        value={tab}
        onChange={(next) => list.set({ tab: next === 'all' ? null : next })}
        // B2cPlatformDenetim: a kind with nothing in it is left out (Tümü and the chosen one stay)
        items={platformAuditTabs
          .filter((id) => id === 'all' || id === tab || !data || (data.tabs[id] ?? 0) > 0)
          .map((id) => ({ id, label: t(`b2cBilling.audit.tabs.${id}`), count: data?.tabs[id], testId: `audit-tab-${id}` }))}
      />
      <div className="dt-toolbar">
        <SearchBox value={list.get('q') ?? ''} onChange={(q) => list.set({ q })} placeholder={t('admin.audit.search')} testId="audit-search" />
        <PersonFilter person={list.get('person')} who={list.get('who')} onChange={(p) => list.set({ person: p?.id ?? null, who: p?.name ?? null })} />
        <FilterMenu
          label={t('admin.audit.date')}
          value={String(days)}
          allLabel={t('admin.audit.dates.d7')}
          options={AUDIT_DAYS.map((d) => ({ value: String(d), label: t(`admin.audit.dates.d${d}`) }))}
          onChange={(value) => list.set({ days: value ?? '7' })}
          testId="audit-days"
        />
        <span className="dt-toolbar__end">
          <CsvLink href={`/api/platform/audit.csv?${query}`} label={t('admin.audit.csv')} testId="audit-export" />
        </span>
      </div>
      <ErrorAlert error={entries.error} />
      {data ? (
        <DataTable
          label={t('platform.audit.title')}
          columns={columns}
          rows={data.rows}
          rowKey={(e) => e.id}
          groupOf={(e) => dayKey(e.createdAt, zone)}
          groupLabel={groupLabel}
          rowClassName={(e) => (view(e).failure ? 'dt__row--failure' : undefined)}
          rowTestId={(e) => `platform-audit-${e.action}`}
          loading={entries.isFetching}
          empty={t('audit.empty')}
          data-testid="platform-audit-table"
          footer={
            <Pagination
              page={list.page}
              size={list.size}
              total={data.total}
              noun={t('admin.audit.noun')}
              note={t('admin.audit.note')}
              onPage={(page) => list.set({ page: String(page) })}
              onSize={(size) => list.set({ size: String(size) })}
            />
          }
        />
      ) : null}
    </div>
  );
}
